import { useMutation, useQueryClient } from '@tanstack/react-query';

import { fbmSubstitutionRulesV1Api } from 'api/fbmSubstitutionRulesV1';
import { ApiError } from 'shared/errors/ApiError';

import { substitutionRuleEventsQueryKeys } from './useSubstitutionRuleEventsQuery';
import { substitutionRulesQueryKeys } from './useSubstitutionRulesQuery';

/**
 * Toggles a substitution rule's enabled status for a facility; on success invalidates the rules list.
 *
 * @param {Omit<
 *   import('@tanstack/react-query').UseMutationOptions<
 *     Object,
 *     ApiError,
 *     { fbmAccountName: string, facilityId: number, ruleGroupId: string, isEnabled: boolean, week: number, year: number }
 *   >,
 *   'mutationFn'
 * >} [mutationOptions={}]
 * @returns {import('@tanstack/react-query').UseMutationResult<
 *   Object,
 *   ApiError,
 *   { fbmAccountName: string, facilityId: number, ruleGroupId: string, isEnabled: boolean, week: number, year: number }
 * >}
 */
export function useToggleRuleFacilityStatusMutation(mutationOptions = {}) {
    const queryClient = useQueryClient();
    const { onSuccess, ...rest } = mutationOptions;

    return useMutation({
        mutationFn: async ({ facilityId, fbmAccountName, isEnabled, ruleGroupId, week, year }) => {
            const response = await fbmSubstitutionRulesV1Api.toggleRuleFacilityStatus(
                fbmAccountName,
                facilityId,
                ruleGroupId,
                isEnabled,
                week,
                year,
            );
            if (!response?.status) {
                throw new ApiError(
                    response?.msg ?? 'Failed to update substitution rule facility status',
                    response?.statusCode,
                    response?.data,
                );
            }
            return response.data;
        },
        onSuccess: (data, variables, ...args) => {
            queryClient.invalidateQueries({
                queryKey: substitutionRulesQueryKeys.list(variables.fbmAccountName, variables.facilityId),
            });
            queryClient.invalidateQueries({
                queryKey: substitutionRuleEventsQueryKeys.list(variables.fbmAccountName, variables.facilityId),
            });
            return onSuccess?.(data, variables, ...args);
        },
        ...rest,
    });
}
